import React, { useState, useEffect } from "react";
import { makeStyles, Typography, CircularProgress, Link } from "@material-ui/core";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// Estado de transcripción (enabled + mode) memoizado a nivel módulo: se pide una
// sola vez por sesión, no por burbuja ni por ticket. Si falla → apagado.
let _statePromise = null;
const fetchTranscriptionState = () => {
  if (!_statePromise) {
    _statePromise = api
      .get("/ai/admin/transcription-state")
      .then((r) => r.data)
      .catch(() => ({ enabled: false, mode: "nunca" }));
  }
  return _statePromise;
};

const useStyles = makeStyles((theme) => ({
  wrap: {
    marginTop: 4,
    paddingTop: 4,
    borderTop: `1px solid ${theme.palette.type === "dark" ? "#2a2f36" : "rgba(0,0,0,0.08)"}`,
  },
  text: {
    fontSize: 12.5,
    lineHeight: 1.35,
    color: theme.palette.type === "dark" ? "#cfd6dd" : "#3b4a54",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  label: {
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    color: theme.palette.type === "dark" ? "#8a949e" : "#667781",
    marginBottom: 1,
  },
  row: { display: "flex", alignItems: "center", gap: 6 },
  muted: { fontSize: 12, color: theme.palette.type === "dark" ? "#8a949e" : "#667781" },
  action: { fontSize: 12, cursor: "pointer" },
  failed: { fontSize: 12, color: "#c0392b" },
}));

const t = (k) => i18n.t(`aiServices.${k}`);

const AudioTranscription = ({ message }) => {
  const classes = useStyles();
  const [state, setState] = useState(null); // {enabled, mode}
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    let active = true;
    fetchTranscriptionState().then((s) => active && setState(s));
    return () => {
      active = false;
    };
  }, []);

  // El status del backend manda: cuando llega 'pending'/'done'/'failed' por
  // socket (UPDATE_MESSAGE), el padre re-renderiza con el nuevo message.
  useEffect(() => {
    if (message.transcriptionStatus === "done" || message.transcriptionStatus === "failed") {
      setRequesting(false);
    }
  }, [message.transcriptionStatus]);

  const requestTranscription = async () => {
    setRequesting(true);
    try {
      await api.post(`/messages/${message.id}/transcribe`);
      // El resultado llega por socket (UPDATE_MESSAGE). No tocamos el estado acá.
    } catch (err) {
      setRequesting(false);
    }
  };

  const status = message.transcriptionStatus;

  // done → mostrar el texto.
  if (status === "done" && message.transcription) {
    return (
      <div className={classes.wrap}>
        <Typography className={classes.label}>{t("transcriptLabel")}</Typography>
        <Typography className={classes.text}>{message.transcription}</Typography>
      </div>
    );
  }

  // pending (o pedido en curso) → spinner.
  if (status === "pending" || requesting) {
    return (
      <div className={classes.wrap}>
        <div className={classes.row}>
          <CircularProgress size={12} />
          <span className={classes.muted}>{t("transcribing")}</span>
        </div>
      </div>
    );
  }

  // failed → mensaje + reintentar (si el servicio sigue habilitado).
  if (status === "failed") {
    return (
      <div className={classes.wrap}>
        <div className={classes.row}>
          <span className={classes.failed}>{t("failedMsg")}</span>
          {state?.enabled && (
            <Link component="button" className={classes.action} onClick={requestTranscription}>
              {t("retry")}
            </Link>
          )}
        </div>
      </div>
    );
  }

  // sin status: botón solo en modo a_pedido habilitado. auto se llena solo;
  // nunca / deshabilitado no muestran nada.
  if (state?.enabled && state.mode === "a_pedido") {
    return (
      <div className={classes.wrap}>
        <Link component="button" className={classes.action} onClick={requestTranscription}>
          {t("transcribeBtn")}
        </Link>
      </div>
    );
  }

  return null;
};

export default AudioTranscription;
